import { alpha, getContrastRatio } from '@mui/system/colorManipulator';
import {
  Palette as MuiPalette,
  PaletteColor as MuiPaletteColor,
  TypeAction as MuiTypeAction,
  TypeBackground as MuiTypeBackground,
} from '@mui/material/styles/createPalette';
import { Color } from '@mui/material';

import { U21Themes } from 'vendor/material-minimal/models';

// ----------------------------------------------------------------------
// CUSTOM CHANGES
// - custom colors
// - removed button text transform
// - set DefaultTheme to ThemeOptions
// - removed secondary color
// - added COLORS
// - added table action colors
// - added typing
// - moved palette mode definition from U21Theme
// - added 50, A100, A200, A400, A700 mui defaults to palette.grey
// - added activatedOpacity, focusOpacity, selectedOpacity mui defaults to palette.action
// - added tonalOffset and contrastThreshold mui defaults to palette
// - added U21Palette
// - add special color

interface CustomPaletteColor {
  lighter: string;
  darker: string;
}

interface CustomTypeBackground {
  neutral: string;
  tableHeader: string;
}

interface CustomTypeAction {
  tableHighlight: string;
  tableHover: string;
  tableSelect: string;
}

interface CustomGrey {
  1000: string;
  500_4: string;
  500_8: string;
  500_10: string;
  500_12: string;
  500_16: string;
  500_24: string;
  500_32: string;
  500_48: string;
  500_56: string;
  500_80: string;
}

export interface U21Palette extends MuiPalette {
  primary: MuiPaletteColor & CustomPaletteColor;
  error: MuiPaletteColor & CustomPaletteColor;
  warning: MuiPaletteColor & CustomPaletteColor;
  info: MuiPaletteColor & CustomPaletteColor;
  success: MuiPaletteColor & CustomPaletteColor;
  special: MuiPaletteColor & CustomPaletteColor;
  colors: Record<
    ColorColorSchema,
    { main: string; dark: string; contrastText: string }
  >;
  grey: Color & CustomGrey;
  action: MuiTypeAction & CustomTypeAction;
  background: MuiTypeBackground & CustomTypeBackground;
  gradients: {
    primary: string;
    info: string;
    success: string;
    warning: string;
    error: string;
    special: string;
  };
  button: { opacity: number };
}

declare module '@mui/material/styles/createPalette' {
  interface TypeAction extends CustomTypeAction {}

  interface TypeBackground extends CustomTypeBackground {}

  interface PaletteColor extends CustomPaletteColor {}

  interface Palette extends Pick<
    U21Palette,
    'button' | 'colors' | 'gradients'
  > {
    special: PaletteColor;
  }
}

declare module '@mui/material' {
  interface Color extends CustomGrey {}
}

export type ColorSchema =
  | 'primary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'special';

export type ColorColorSchema =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'olive'
  | 'teal'
  | 'blue'
  | 'purple'
  | 'brown'
  | 'grey'
  | 'black';

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

const getContrastText = (
  color: string,
  lightContrastText: string,
  darkContrastText: string,
): string =>
  getContrastRatio(color, lightContrastText) >= CONTRAST_THRESHOLD
    ? lightContrastText
    : darkContrastText;

const CONTRAST_THRESHOLD = 3;

const LIGHT_MODE_GREY: U21Palette['grey'] = {
  50: '#FAFAFA',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  1000: '#121212',
  500_4: alpha('#919EAB', 0.04),
  500_8: alpha('#919EAB', 0.08),
  500_10: alpha('#919EAB', 0.1),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
  A100: '#F5F5F5',
  A200: '#EEEEEE',
  A400: '#BDBDBD',
  A700: '#616161',
};

// not using getContrastText due to legacy color combination
const LIGHT_MODE_BASE_COLORS: Pick<
  U21Palette,
  'primary' | 'info' | 'success' | 'warning' | 'error' | 'special'
> = {
  primary: {
    lighter: '#D0F2FF',
    light: '#74CAFF',
    main: '#1890FF',
    dark: '#0C53B7',
    darker: '#04297A',
    contrastText: '#fff',
  },
  info: {
    lighter: '#D3EDEE',
    light: '#6BC2C7',
    main: '#43AAB1',
    dark: '#328185',
    darker: '#225559',
    contrastText: '#fff',
  },
  success: {
    lighter: '#EFFFBE',
    light: '#C3EB45',
    main: '#A3D211',
    dark: '#7CA00E',
    darker: '#495F06',
    contrastText: LIGHT_MODE_GREY[800],
  },
  warning: {
    lighter: '#FFF7CD',
    light: '#FFE16A',
    main: '#FFC107',
    dark: '#B78103',
    darker: '#7A4F01',
    contrastText: LIGHT_MODE_GREY[800],
  },
  error: {
    lighter: '#FFD6C4',
    light: '#FFA386',
    main: '#FF6158',
    dark: '#CE473F',
    darker: '#8D231F',
    contrastText: '#fff',
  },
  special: {
    lighter: '#F9D4FF',
    light: '#EB71FF',
    main: '#9C3CAC',
    dark: '#7D1F8D',
    darker: '#660376',
    contrastText: '#fff',
  },
};

const DARK_MODE_GREY: U21Palette['grey'] = {
  50: '#FAFAFA',
  100: '#F0F0F0', //  → 240
  200: '#ECECEC', //  → 236  (−4)
  300: '#D8D8D8', //  → 216  (−20)
  400: '#C2C2C2', //  → 194  (−22)
  500: '#919191', //  → 145  (−49)
  600: '#636363', //  → 99   (−46)
  700: '#404040', //  → 64   (−35)
  800: '#1A1A1A', //  → 26   (−38)
  900: '#0B0B0B', //  → 11   (−15)
  1000: '#000000', // → 0    (−11)
  500_4: alpha('#919191', 0.04),
  500_8: alpha('#919191', 0.08),
  500_10: alpha('#919191', 0.1),
  500_12: alpha('#919191', 0.12),
  500_16: alpha('#919191', 0.16),
  500_24: alpha('#919191', 0.24),
  500_32: alpha('#919191', 0.32),
  500_48: alpha('#919191', 0.48),
  500_56: alpha('#919191', 0.56),
  500_80: alpha('#919191', 0.8),
  A100: '#F5F5F5',
  A200: '#EEEEEE',
  A400: '#BDBDBD',
  A700: '#616161',
};

const DARK_MODE_BASE_COLORS: typeof LIGHT_MODE_BASE_COLORS = {
  primary: {
    lighter: '#D0F2FF',
    light: '#74CAFF',
    main: '#1890FF',
    dark: '#0C53B7',
    darker: '#04297A',
    contrastText: getContrastText('#1890FF', '#fff', '#000'),
  },
  info: {
    lighter: '#D3EDEE',
    light: '#6BC2C7',
    main: '#43AAB1',
    dark: '#328185',
    darker: '#225559',
    contrastText: getContrastText('#43AAB1', '#fff', '#000'),
  },
  success: {
    lighter: '#EFFFBE',
    light: '#C3EB45',
    main: '#A3D211',
    dark: '#7CA00E',
    darker: '#495F06',
    contrastText: getContrastText('#A3D211', '#fff', '#000'),
  },
  warning: {
    lighter: '#FFF7CD',
    light: '#FFE16A',
    main: '#FFC107',
    dark: '#B78103',
    darker: '#7A4F01',
    contrastText: getContrastText('#FFC107', '#fff', '#000'),
  },
  error: {
    lighter: '#FFD6C4',
    light: '#FFA386',
    main: '#FF6158',
    dark: '#CE473F',
    darker: '#8D231F',
    contrastText: getContrastText('#FF6158', '#fff', '#000'),
  },
  special: {
    lighter: '#F9D4FF',
    light: '#EB71FF',
    main: '#9C3CAC',
    dark: '#7D1F8D',
    darker: '#660376',
    contrastText: getContrastText('#9C3CAC', '#fff', '#000'),
  },
};

const createColors = (
  baseColors: typeof LIGHT_MODE_BASE_COLORS,
  lightContrastText: string,
  darkContrastText: string,
): Pick<U21Palette, 'colors' | 'gradients'> => ({
  colors: {
    red: {
      main: baseColors.error.main,
      dark: baseColors.error.dark,
      contrastText: getContrastText(
        baseColors.error.main,
        lightContrastText,
        darkContrastText,
      ),
    },
    orange: {
      main: '#FF8A00',
      dark: '#D27404',
      contrastText: getContrastText(
        '#FF8A00',
        lightContrastText,
        darkContrastText,
      ),
    },
    yellow: {
      main: baseColors.warning.main,
      dark: baseColors.warning.dark,
      contrastText: getContrastText(
        baseColors.warning.main,
        lightContrastText,
        darkContrastText,
      ),
    },
    green: {
      main: baseColors.success.main,
      dark: baseColors.success.dark,
      contrastText: getContrastText(
        baseColors.success.main,
        lightContrastText,
        darkContrastText,
      ),
    },
    olive: {
      main: '#495F06',
      dark: '#2B3802',
      contrastText: getContrastText(
        '#495F06',
        lightContrastText,
        darkContrastText,
      ),
    },
    teal: {
      main: '#43AAB1',
      dark: '#328185',
      contrastText: getContrastText(
        '#43AAB1',
        lightContrastText,
        darkContrastText,
      ),
    },
    blue: {
      main: baseColors.primary.main,
      dark: baseColors.primary.dark,
      contrastText: getContrastText(
        baseColors.primary.main,
        lightContrastText,
        darkContrastText,
      ),
    },
    purple: {
      main: baseColors.special.main,
      dark: baseColors.special.dark,
      contrastText: getContrastText(
        baseColors.special.main,
        lightContrastText,
        darkContrastText,
      ),
    },
    brown: {
      main: '#7A4F01',
      dark: '#593A00',
      contrastText: getContrastText(
        '#7A4F01',
        lightContrastText,
        darkContrastText,
      ),
    },
    grey: {
      main: '#637381',
      dark: '#454F5B',
      contrastText: getContrastText(
        '#637381',
        lightContrastText,
        darkContrastText,
      ),
    },
    black: {
      main: '#212B36',
      dark: '#161C24',
      contrastText: getContrastText(
        '#212B36',
        lightContrastText,
        darkContrastText,
      ),
    },
  },
  gradients: {
    primary: createGradient(baseColors.primary.light, baseColors.primary.main),
    info: createGradient(baseColors.info.light, baseColors.info.main),
    success: createGradient(baseColors.success.light, baseColors.success.main),
    warning: createGradient(baseColors.warning.light, baseColors.warning.main),
    error: createGradient(baseColors.error.light, baseColors.error.main),
    special: createGradient(baseColors.special.light, baseColors.special.main),
  },
});

const CREAM = {
  darker_3: '#FFEA9C',
  darker_2: '#FFEDAD',
  darker_1: '#FFF0B9',
  main: '#FFF4CE',
  lighter_1: '#FFF7DA',
  lighter_2: '#FFFAE8',
  lighter_3: '#FFFCF2',
  lighter_4: '#FFFEFA',
};

const palette: Record<
  U21Themes,
  Omit<U21Palette, 'augmentColor' | 'secondary'>
> = {
  light: {
    common: { black: '#000', white: '#fff' },
    ...LIGHT_MODE_BASE_COLORS,
    ...createColors(LIGHT_MODE_BASE_COLORS, '#fff', LIGHT_MODE_GREY[800]),
    grey: LIGHT_MODE_GREY,
    divider: LIGHT_MODE_GREY[500_24],
    tonalOffset: 0.2,
    contrastThreshold: CONTRAST_THRESHOLD,
    text: {
      primary: LIGHT_MODE_GREY[800],
      secondary: LIGHT_MODE_GREY[600],
      disabled: LIGHT_MODE_GREY[500],
    },
    background: {
      paper: '#fff',
      default: '#fff',
      neutral: LIGHT_MODE_GREY[200],
      tableHeader: LIGHT_MODE_GREY[300],
    },
    action: {
      disabled: LIGHT_MODE_GREY[500_80],
      disabledBackground: LIGHT_MODE_GREY[500_24],
      disabledOpacity: 0.48,
      focus: LIGHT_MODE_GREY[500_24],
      focusOpacity: 0.12,
      hoverOpacity: 0.08,
      selected: LIGHT_MODE_GREY[500_16],
      activatedOpacity: 0.12,
      active: LIGHT_MODE_GREY[600],
      hover: LIGHT_MODE_GREY[300],
      selectedOpacity: 0.08,
      tableHover: '#F7FBFF', // 4% primary against background.paper
      tableHighlight: '#EFF6FF', // 8% primary against background.paper
      tableSelect: '#E6F1FF', // 12% primary against background.paper
    },
    button: { opacity: 1 },
    mode: 'light',
    getContrastText: (color: string) =>
      getContrastText(color, '#fff', LIGHT_MODE_GREY[800]),
  },
  dark: {
    common: { black: '#000', white: '#fff' },
    ...DARK_MODE_BASE_COLORS,
    ...createColors(DARK_MODE_BASE_COLORS, '#fff', '#000'),
    grey: DARK_MODE_GREY,
    divider: DARK_MODE_GREY[500_24],
    tonalOffset: 0.2,
    contrastThreshold: CONTRAST_THRESHOLD,
    text: {
      primary: CREAM.main,
      secondary: DARK_MODE_GREY[400],
      disabled: DARK_MODE_GREY[600],
    },
    background: {
      paper: DARK_MODE_GREY[800],
      default: DARK_MODE_GREY[800],
      neutral: DARK_MODE_GREY[900],
      tableHeader: DARK_MODE_GREY[900],
    },
    action: {
      disabled: DARK_MODE_GREY[500_80],
      disabledBackground: DARK_MODE_GREY[500_24],
      disabledOpacity: 0.48,
      focus: DARK_MODE_GREY[500_24],
      focusOpacity: 0.12,
      hoverOpacity: 0.08,
      selected: DARK_MODE_GREY[500_16],
      activatedOpacity: 0.24,
      active: DARK_MODE_GREY[500],
      hover: DARK_MODE_GREY[500_8],
      selectedOpacity: 0.16,
      tableHighlight: '#1E2D40', // 16% primary against background.paper
      tableHover: '#1B232C', // 8% primary against background.paper
      tableSelect: '#1E3751', // 24% primary against background.paper
    },
    button: { opacity: 0.9 },
    mode: 'dark',
    getContrastText: (color: string) => getContrastText(color, '#fff', '#000'),
  },
};

export default palette;
